import { useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";
import { CopyButton } from "@/components/kit/KitFields";

const SRC = { final: "Final resume", bank: "Bullet bank", ai: "New", edited: "Edited" };
const icon = "p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-20 disabled:pointer-events-none transition-colors";

export default function BulletRow({ it, idx, total, picked, kit, act, lit, onHover }) {
  const [edit, setEdit] = useState(null);
  const bad = it.issues.length > 0;
  const tid = `bullet-${it.id}`;
  const save = async () => { if (await act.edit(it.id, edit)) setEdit(null); };
  return (
    <div data-testid={tid} onMouseEnter={() => onHover?.(it.origin?.key || null)} onMouseLeave={() => onHover?.(null)}
      className={`flex gap-3 p-3 rounded-lg border transition-colors ${lit ? "ring-1 ring-blue-500/70" : ""} ${bad ? "border-rose-500/50 bg-rose-500/5" : picked ? "border-blue-500/30 bg-blue-500/[0.04]" : "border-border bg-slate-900/30 opacity-60"}`}>
      <div className="w-9 shrink-0 text-center">
        <div className={`text-sm font-bold font-mono ${picked && !bad ? "text-blue-300" : "text-slate-500"}`} data-testid={`${tid}-rank`}>#{idx + 1}</div>
        <div className="text-[9px] uppercase tracking-wide text-slate-500 leading-tight mt-0.5">{bad ? "Needs fix" : idx === 0 ? "Most relevant" : picked ? "On resume" : "Not used"}</div>
      </div>
      <div className="flex-1 min-w-0">
        {edit === null ? <div className="text-sm text-slate-100 leading-relaxed" data-testid={`${tid}-text`}>{it.text}</div> : (
          <div className="space-y-2">
            <textarea data-testid={`${tid}-edit-input`} value={edit} onChange={(e) => setEdit(e.target.value)} rows={3}
              className="w-full bg-slate-900 border border-border rounded-md p-2 text-sm outline-none focus:border-blue-500" />
            <div className="flex gap-2">
              <button data-testid={`${tid}-edit-save`} onClick={save} className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white">Save</button>
              <button data-testid={`${tid}-edit-cancel`} onClick={() => setEdit(null)} className="text-xs px-3 py-1.5 rounded-md border border-border text-slate-300">Cancel</button>
            </div>
          </div>
        )}
        {it.origin && (
          <div className="mt-1.5 text-[11px] text-slate-400 border-l-2 border-slate-700 pl-2" data-testid={`${tid}-origin`}>
            <span className="text-slate-500">Based on {it.origin.kind === "resume" ? "original resume" : "client fact"}:</span> {it.origin.text}
            {it.origin.also?.length > 0 && <div data-testid={`${tid}-origin-also`}><span className="text-slate-500">Numbers also from client facts:</span> {it.origin.also.join(" · ")}</div>}
          </div>
        )}
        {!it.origin && <div className="mt-1.5 text-[11px] text-amber-300" data-testid={`${tid}-no-origin`}>No matching original point found: check this bullet against the client's resume.</div>}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
          <span>{SRC[it.source] || it.source}</span><span>{it.text.length} chars</span>
          {it.keywords_used?.length > 0 && <span className="truncate">Keywords: {it.keywords_used.slice(0, 4).join(", ")}</span>}
        </div>
        {bad && <div className="mt-1.5 text-xs text-rose-300 flex gap-1.5" data-testid={`${tid}-issue`}><AlertTriangle size={13} className="shrink-0 mt-0.5" />{it.issues.join(". ")}. Edit it before copying.</div>}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <CopyButton text={it.text} k={`b:${it.id}:${it.text.length}`} kit={kit} testid={`${tid}-copy`} disabled={bad} />
        <div className="flex">
          <button data-testid={`${tid}-up`} aria-label="Move up" disabled={idx === 0} onClick={() => act.move(idx, -1)} className={icon}><ArrowUp size={14} /></button>
          <button data-testid={`${tid}-down`} aria-label="Move down" disabled={idx === total - 1} onClick={() => act.move(idx, 1)} className={icon}><ArrowDown size={14} /></button>
          <button data-testid={`${tid}-edit`} aria-label="Edit" onClick={() => setEdit(it.text)} className={icon}><Pencil size={14} /></button>
          <button data-testid={`${tid}-delete`} aria-label="Remove" onClick={() => act.remove(it.id)} className={`${icon} hover:text-rose-300`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}
